import React, { useState } from 'react';
import MainBlockLayout from '../../components/MainBlockLayout';
import Accordion from 'react-bootstrap/Accordion';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import MapWithMarkers from './components/MapWithMarkers';
import './Admissions.css';

/*
  HOW TO EDIT THIS PAGE
  - STEPS: the interactive "How to register" guide. Each step uses translation keys
    (title, description, notes). Add or remove a step by editing this list.
  - FACTS: the three quick facts under the header (value + label translation keys).
  - FAQ_COUNT: number of questions. Questions use keys admissions.question1..N / answer1..N.
*/
const STEPS = [
  {
    title: 'admissions.step_one',
    desc: 'admissions.step_one_desc',
    notes: ['admissions.step_one_note1', 'admissions.step_one_note2'],
    // Scrolls down to the map section below
    action: { href: '#admissions-map', label: 'admissions_go_map' },
  },
  {
    title: 'admissions.step_two',
    desc: 'admissions.step_two_desc',
    notes: ['admissions.step_two_note'],
  },
  {
    title: 'admissions.step_three',
    desc: 'admissions.step_three_desc',
    notes: ['admissions.step_three_note'],
  },
];

const FACTS = [
  { icon: '💷', value: 'admissions_fact_fee_value', label: 'admissions_fact_fee_label' },
  { icon: '✉️', value: 'admissions_fact_reply_value', label: 'admissions_fact_reply_label' },
];

const FAQ_COUNT = 11;

function MainBlock() {
  // Index of the step currently shown in the guide
  const [step, setStep] = useState(0);
  const current = STEPS[step];

  return (
    <div className="admissionsPage">
      <div className="admissionsHero">
        <h2><Trans i18nKey="Admissions" /></h2>
        <p><Trans i18nKey="admissions.welcome_text" /></p>
      </div>

      <div className="admissionsFacts">
        {FACTS.map((fact) => (
          <div key={fact.label} className="admissionsFact">
            <span className="admissionsFactIcon" aria-hidden="true">{fact.icon}</span>
            <strong><Trans i18nKey={fact.value} /></strong>
            <span><Trans i18nKey={fact.label} /></span>
          </div>
        ))}
      </div>

      {/* Temporary notice: delete this block when the admissions team is back to normal hours */}
      <div className="admissionsNotice" role="note">
        <span aria-hidden="true">ℹ️</span>
        <p><Trans i18nKey="admissions_notice" /></p>
      </div>

      {/* Interactive guide: click a number or use Back / Next */}
      <h3 className="admissionsTitle"><Trans i18nKey="admissions.how_register" /></h3>
      <p className="admissionsSubtitle"><Trans i18nKey="admissions.step_guide" /></p>

      <div className="stepper">
        <div className="stepperDots">
          {STEPS.map((s, i) => (
            <button
              key={s.title}
              type="button"
              className={`stepperDot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
              onClick={() => setStep(i)}
              aria-label={`Step ${i + 1}`}
              aria-current={i === step ? 'step' : undefined}
            >
              {i < step ? '✓' : i + 1}
            </button>
          ))}
        </div>

        <div className="stepperPanel">
          <h4><Trans i18nKey={current.title} /></h4>
          <p><Trans i18nKey={current.desc} /></p>
          <ul>
            {current.notes.map((note) => (
              <li key={note}><Trans i18nKey={note} /></li>
            ))}
          </ul>
          {current.action && (
            <a className="stepperAction" href={current.action.href}>
              <Trans i18nKey={current.action.label} />
            </a>
          )}
        </div>

        <div className="stepperButtons">
          <button type="button" className="stepperBtn" disabled={step === 0} onClick={() => setStep(step - 1)}>
            ← <Trans i18nKey="admissions_back" />
          </button>
          <button type="button" className="stepperBtn stepperBtnPrimary" disabled={step === STEPS.length - 1} onClick={() => setStep(step + 1)}>
            <Trans i18nKey="admissions_next" /> →
          </button>
        </div>
      </div>

      <div id="admissions-map" className="admissionsMap">
        <MapWithMarkers />
      </div>

      <div className="admissionsCenter">
        <Link className="admissionsLocationsButton" to="/satellite-schools">
          <Trans i18nKey="admissions.all_locations_button" />
        </Link>
      </div>

      <h3 className="admissionsTitle"><Trans i18nKey="admissions.faq" /></h3>
      <Accordion defaultActiveKey="0" className="custom-accordion mb-5">
        {Array.from({ length: FAQ_COUNT }, (_, i) => i + 1).map((n) => (
          <Accordion.Item eventKey={String(n - 1)} key={n}>
            <Accordion.Header><Trans i18nKey={`admissions.question${n}`} /></Accordion.Header>
            <Accordion.Body className="text-start">
              <Trans i18nKey={`admissions.answer${n}`} />
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
}

export default MainBlockLayout(MainBlock);
